import { useRef, useCallback } from "react";

// Shared physical-control widgets for the LiveView control column - a
// rotary knob for analog channels, an Indian-style rocker switch + LED
// indicator for digital ones. Kept separate from DeviceMonitorControl.jsx
// since the drag-angle math and switch geometry are self-contained and
// don't need any of that file's signal-type plumbing.

// A real pot doesn't turn a full circle - it sweeps 300° and dead-ends at
// both stops, with a 60° gap at the bottom where the shaft can't go. Angles
// below are measured clockwise from straight up (12 o'clock = 0deg).
const KNOB_MIN_ANGLE = -150;
const KNOB_MAX_ANGLE = 150;
const KNOB_SWEEP = KNOB_MAX_ANGLE - KNOB_MIN_ANGLE;

function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}

function valueToAngle(value) {
    return KNOB_MIN_ANGLE + (clamp(value, 0, 100) / 100) * KNOB_SWEEP;
}

// Inverse of valueToAngle's polar mapping - screen dx/dy (pointer relative
// to the knob's center) to a clockwise-from-top angle in (-180, 180].
function pointerAngle(dx, dy) {
    return Math.atan2(dx, -dy) * (180 / Math.PI);
}

// Angle -> 0-100, with the dead zone (anything past a stop, all the way
// around to the other stop) clamped to whichever stop it's nearest -
// exactly how a real pot behaves when you keep turning past its limit.
function angleToValue(angle) {
    const clamped =
        angle < KNOB_MIN_ANGLE
            ? KNOB_MIN_ANGLE
            : angle > KNOB_MAX_ANGLE
              ? KNOB_MAX_ANGLE
              : angle;
    return ((clamped - KNOB_MIN_ANGLE) / KNOB_SWEEP) * 100;
}

function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
}

// A classic instrument needle: a thin kite from a short tail, through the
// pivot, out to a point - not just a straight line, so it actually reads as
// a needle rather than a hand on a clock.
function needleKitePoints(cx, cy, angleDeg, frontLen, tailLen, halfWidth) {
    const rad = (angleDeg * Math.PI) / 180;
    const dx = Math.sin(rad);
    const dy = -Math.cos(rad);
    const px = Math.cos(rad);
    const py = Math.sin(rad);
    const tip = { x: cx + dx * frontLen, y: cy + dy * frontLen };
    const tail = { x: cx - dx * tailLen, y: cy - dy * tailLen };
    const base1 = { x: cx + px * halfWidth, y: cy + py * halfWidth };
    const base2 = { x: cx - px * halfWidth, y: cy - py * halfWidth };
    return `${base1.x},${base1.y} ${tip.x},${tip.y} ${base2.x},${base2.y} ${tail.x},${tail.y}`;
}

const KNOB_SIZE = 140;
const KNOB_CENTER = KNOB_SIZE / 2;
const KNOB_R = KNOB_CENTER - 8;

export function RotaryKnob({
    value,
    onChange,
    interactive = false,
    color = "#0080ff",
    size = KNOB_SIZE,
}) {
    const svgRef = useRef(null);
    const scale = size / KNOB_SIZE;

    const updateFromPointer = useCallback(
        (clientX, clientY) => {
            const rect = svgRef.current.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            const angle = pointerAngle(clientX - cx, clientY - cy);
            onChange(Math.round(angleToValue(angle)));
        },
        [onChange],
    );

    const handlePointerDown = (e) => {
        if (!interactive) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        updateFromPointer(e.clientX, e.clientY);
    };
    const handlePointerMove = (e) => {
        if (!interactive || e.buttons === 0) return;
        updateFromPointer(e.clientX, e.clientY);
    };

    const pointerAngleDeg = valueToAngle(value);
    const tickValues = Array.from({ length: 11 }, (_, i) => i * 10);
    const minorTickValues = Array.from({ length: 41 }, (_, i) => i * 2.5).filter(
        (v) => v % 10 !== 0,
    );

    // A sense-only channel can't be turned, so it gets a gauge - a real
    // circular pressure-gauge read: chrome bezel, glass reflection, a white
    // dial with the tick ring printed near its own edge, and a tapered
    // needle - instead of a knob's raised grippable body with ticks in a
    // separate ring outside it. Same underlying tick/angle math either way,
    // just different radii so each reads as the right kind of instrument.
    const isGauge = !interactive;
    const faceR = KNOB_R * (isGauge ? 0.9 : 0.72);
    const tickOuterR = isGauge ? faceR * 0.96 : KNOB_R * 1.0;
    const tickMajorInnerR = isGauge ? faceR * 0.8 : KNOB_R * 0.88;
    const tickMinorInnerR = isGauge ? faceR * 0.88 : KNOB_R * 0.94;
    const labelR = isGauge ? faceR * 0.64 : KNOB_R * 1.14;
    const needleFrontLen = isGauge ? faceR * 0.82 : KNOB_R * 0.62;
    const needleTip = polarToCartesian(KNOB_CENTER, KNOB_CENTER, needleFrontLen, pointerAngleDeg);
    const needleBase = polarToCartesian(KNOB_CENTER, KNOB_CENTER, KNOB_R * 0.18, pointerAngleDeg);

    return (
        <svg
            ref={svgRef}
            width={size}
            height={size}
            viewBox={`0 0 ${KNOB_SIZE} ${KNOB_SIZE}`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            className={interactive ? "tw-cursor-grab active:tw-cursor-grabbing" : ""}
            style={{ touchAction: "none", transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
            <defs>
                <radialGradient id="knobBody" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#f5f5f5" />
                    <stop offset="55%" stopColor="#d8d8dc" />
                    <stop offset="100%" stopColor="#9a9aa2" />
                </radialGradient>
                <radialGradient id="knobBezel" cx="50%" cy="50%" r="50%">
                    <stop offset="80%" stopColor="#6b6b73" />
                    <stop offset="100%" stopColor="#3f3f45" />
                </radialGradient>
                <radialGradient id="gaugeFace" cx="42%" cy="38%" r="75%">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="70%" stopColor="#fbfbfc" />
                    <stop offset="100%" stopColor="#e4e4e9" />
                </radialGradient>
                {/* Chrome bezel - a light/dark/light sweep is what actually
                    reads as polished metal rather than flat gray. */}
                <linearGradient id="chromeBezel" x1="20%" y1="10%" x2="80%" y2="90%">
                    <stop offset="0%" stopColor="#f2f2f5" />
                    <stop offset="30%" stopColor="#aeaeb6" />
                    <stop offset="50%" stopColor="#7c7c85" />
                    <stop offset="70%" stopColor="#c9c9d0" />
                    <stop offset="100%" stopColor="#8c8c94" />
                </linearGradient>
                <clipPath id="gaugeFaceClip">
                    <circle cx={KNOB_CENTER} cy={KNOB_CENTER} r={faceR} />
                </clipPath>
            </defs>

            {/* Bezel - chrome ring for a gauge, dark plastic ring for a
                knob's own mounting collar. */}
            <circle
                cx={KNOB_CENTER} cy={KNOB_CENTER} r={KNOB_R}
                fill={isGauge ? "url(#chromeBezel)" : "url(#knobBezel)"}
            />
            {isGauge && (
                <circle
                    cx={KNOB_CENTER} cy={KNOB_CENTER} r={KNOB_R * 0.94}
                    fill="none" stroke="#6a6a72" strokeWidth="1"
                />
            )}

            {/* Face */}
            <circle
                cx={KNOB_CENTER}
                cy={KNOB_CENTER}
                r={faceR}
                fill={isGauge ? "url(#gaugeFace)" : "url(#knobBody)"}
                stroke="#88888f"
                strokeWidth="1"
            />

            {/* Tick marks - minor first so majors draw on top */}
            {minorTickValues.map((v) => {
                const a = valueToAngle(v);
                const p1 = polarToCartesian(KNOB_CENTER, KNOB_CENTER, tickMinorInnerR, a);
                const p2 = polarToCartesian(KNOB_CENTER, KNOB_CENTER, tickOuterR, a);
                return (
                    <line
                        key={`minor-${v}`}
                        x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                        stroke={isGauge ? "#8a8a92" : "#c7c7cf"} strokeWidth="1"
                    />
                );
            })}
            {tickValues.map((v) => {
                const a = valueToAngle(v);
                const p1 = polarToCartesian(KNOB_CENTER, KNOB_CENTER, tickMajorInnerR, a);
                const p2 = polarToCartesian(KNOB_CENTER, KNOB_CENTER, tickOuterR, a);
                const labelPos = polarToCartesian(KNOB_CENTER, KNOB_CENTER, labelR, a);
                const showLabel = isGauge ? v % 20 === 0 : v === 0 || v === 50 || v === 100;
                return (
                    <g key={`major-${v}`}>
                        <line
                            x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                            stroke={isGauge ? "#3a3a40" : "#e8e8ee"}
                            strokeWidth={isGauge ? "1.5" : "2"}
                        />
                        {showLabel && (
                            <text
                                x={labelPos.x}
                                y={labelPos.y}
                                fontSize={isGauge ? "10" : "9"}
                                fontWeight={isGauge ? "600" : "400"}
                                fill={isGauge ? "#333338" : "#8a8a92"}
                                textAnchor="middle"
                                dominantBaseline="middle"
                            >
                                {v}
                            </text>
                        )}
                    </g>
                );
            })}

            {/* Knurled grip rim - a knob's own "you can turn this" cue, so
                it's deliberately left off the gauge. */}
            {!isGauge &&
                Array.from({ length: 24 }, (_, i) => i * 15).map((a) => {
                    const p1 = polarToCartesian(KNOB_CENTER, KNOB_CENTER, KNOB_R * 0.64, a);
                    const p2 = polarToCartesian(KNOB_CENTER, KNOB_CENTER, KNOB_R * 0.7, a);
                    return (
                        <line
                            key={`knurl-${a}`}
                            x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                            stroke="#b8b8c0" strokeWidth="1.5"
                        />
                    );
                })}

            {isGauge ? (
                <>
                    {/* Tapered instrument needle in the channel's own
                        color - the one accent on an otherwise stock
                        pressure-gauge face. */}
                    <polygon
                        points={needleKitePoints(
                            KNOB_CENTER, KNOB_CENTER, pointerAngleDeg,
                            needleFrontLen, faceR * 0.18, 2.6,
                        )}
                        fill={color}
                    />
                    <circle cx={KNOB_CENTER} cy={KNOB_CENTER} r="5" fill="#3a3a40" />
                    <circle cx={KNOB_CENTER} cy={KNOB_CENTER} r="2" fill="#c9c9d0" />
                    {/* Unit label, same spot a real gauge prints its brand/
                        unit text - a small fixed "%" since every channel
                        here reads on the same 0-100 scale. */}
                    <text
                        x={KNOB_CENTER}
                        y={KNOB_CENTER + faceR * 0.4}
                        fontSize="8"
                        fill="#9a9aa2"
                        textAnchor="middle"
                    >
                        %
                    </text>
                    {/* Glass reflection - clipped to the face so it never
                        spills onto the bezel. */}
                    <g clipPath="url(#gaugeFaceClip)">
                        <ellipse
                            cx={KNOB_CENTER - faceR * 0.1}
                            cy={KNOB_CENTER - faceR * 0.5}
                            rx={faceR * 0.85}
                            ry={faceR * 0.45}
                            fill="#ffffff"
                            opacity="0.22"
                            transform={`rotate(-18 ${KNOB_CENTER} ${KNOB_CENTER})`}
                        />
                    </g>
                </>
            ) : (
                <>
                    {/* Pointer/indicator line - the one part that carries
                        the channel's own color, same convention LiveView's
                        graph line and the signal-nav dot already use. */}
                    <line
                        x1={needleBase.x} y1={needleBase.y}
                        x2={needleTip.x} y2={needleTip.y}
                        stroke={color} strokeWidth="4" strokeLinecap="round"
                    />
                    <circle cx={needleBase.x} cy={needleBase.y} r="3" fill={color} />
                </>
            )}
        </svg>
    );
}

const SWITCH_SIZE = 72;

// A single-gang Indian modular switch (Anchor Roma/Legrand Mylinc/Havells
// Crabtree style): a nearly-square plate with mounting screws top and
// bottom, and ONE rocker paddle that seesaws about its horizontal center -
// not a Western up/down toggle, and not a two-tone split face - a single
// piece that tilts as a whole, with its far edge lifting as the near edge
// presses in. The tilt is a real 3D rotation (not just a shadow swap) so
// the on/off movement actually reads as movement.
export function RockerSwitch({
    on,
    onToggle,
    interactive = false,
    color = "#0080ff",
}) {
    return (
        <button
            type="button"
            disabled={!interactive}
            onClick={interactive ? onToggle : undefined}
            aria-pressed={on}
            title={interactive ? "Click to toggle" : undefined}
            className={`tw-relative tw-p-0 tw-border-0 tw-bg-transparent ${
                interactive ? "tw-cursor-pointer" : "tw-cursor-default"
            }`}
            style={{ width: SWITCH_SIZE, height: SWITCH_SIZE }}
        >
            {/* Mounting plate */}
            <div
                className="tw-relative tw-w-full tw-h-full"
                style={{
                    borderRadius: 10,
                    background: "linear-gradient(165deg, #ffffff 0%, #f2f2f4 55%, #e2e2e7 100%)",
                    boxShadow:
                        "0 2px 6px rgba(0,0,0,0.16), inset 0 0 0 1px #d2d2d8",
                }}
            >
                {/* Mounting screws, top and bottom - the one detail that
                    instantly reads as "modular switch plate" rather than a
                    generic rounded square. */}
                {[6, SWITCH_SIZE - 10].map((top) => (
                    <div
                        key={top}
                        className="tw-absolute tw-left-1/2 -tw-translate-x-1/2 tw-rounded-full"
                        style={{
                            top,
                            width: 4,
                            height: 4,
                            background:
                                "linear-gradient(135deg, #c4c4cc, #8a8a92)",
                            boxShadow: "0 0.5px 1px rgba(0,0,0,0.4)",
                        }}
                    />
                ))}

                {/* Rocker cavity - a recessed well the paddle sits inside,
                    same as the real fitting's cut-out. */}
                <div
                    className="tw-absolute tw-rounded-md"
                    style={{
                        inset: 13,
                        background: "#d6d6dc",
                        boxShadow: "inset 0 1px 4px rgba(0,0,0,0.35)",
                    }}
                >
                    {/* The rocker paddle itself */}
                    <div
                        className="tw-absolute tw-inset-0.5 tw-rounded"
                        style={{
                            background:
                                "linear-gradient(180deg, #ffffff 0%, #f4f4f6 100%)",
                            border: "1px solid #cacad0",
                            transform: `perspective(220px) rotateX(${
                                on ? -9 : 9
                            }deg)`,
                            transformOrigin: "center",
                            transition: "transform 180ms ease-out, box-shadow 180ms ease-out",
                            boxShadow: on
                                ? "0 3px 5px rgba(0,0,0,0.18), inset 0 7px 8px -5px rgba(0,0,0,0.28)"
                                : "0 -3px 5px rgba(0,0,0,0.1), inset 0 -7px 8px -5px rgba(0,0,0,0.28)",
                        }}
                    >
                        {/* Center seam - the rocker's own pivot line */}
                        <div
                            className="tw-absolute tw-left-2 tw-right-2 tw-top-1/2 -tw-translate-y-1/2"
                            style={{ borderTop: "1px solid #dedee2" }}
                        />
                        {/* Channel color accent - a small printed dot, the
                            one hint of color on an otherwise stock white
                            fitting. */}
                        <div
                            className="tw-absolute tw-left-1/2 tw-bottom-1.5 -tw-translate-x-1/2 tw-rounded-full tw-transition-colors tw-duration-200"
                            style={{
                                width: 5,
                                height: 5,
                                background: on ? color : "#c7c7cf",
                            }}
                        />
                    </div>
                </div>
            </div>
        </button>
    );
}

// The small indicator strip built into an actuate switch's own faceplate
// (the thin glowing bar on a real modular switch, separate from the rocker
// itself) - a horizontal bar, not the switch's position. Outlined-only in
// the channel color when off; a solid filled bar when on, same "hollow vs
// filled" convention as everything else in this panel that shows on/off.
export function LedIndicator({ on, color = "#0080ff" }) {
    return (
        <div
            className="tw-rounded-full tw-transition-all tw-duration-200"
            style={{
                width: 32,
                height: 6,
                border: `2px solid ${color}`,
                backgroundColor: on ? color : "transparent",
                boxShadow: on ? `0 0 6px ${color}99` : "none",
            }}
            title={on ? "ON" : "OFF"}
        />
    );
}

const PILOT_LAMP_SIZE = 44;
const PILOT_C = 50; // viewBox is 0 0 100 100

// A read-only digital channel's own indicator - a panel-mount LED pilot
// lamp (front view only), the AD16-22-style indicator used on control
// panels, outside an operation theatre, or on a geyser/heater's own
// indicator: a knurled dark plastic mounting bezel around a domed lens with
// concentric fresnel ripples, dark and flat when off, glowing with a glassy
// highlight when on. Never a switch - there's nothing to actuate here, only
// a state to read.
export function PilotLamp({ on, color = "#0080ff", size = PILOT_LAMP_SIZE }) {
    const ripples = [10, 17, 24, 30];
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 100 100"
            style={{ display: "block" }}
        >
            <title>{on ? "ON" : "OFF"}</title>
            <defs>
                <radialGradient id="lampBezel" cx="35%" cy="30%" r="75%">
                    <stop offset="0%" stopColor="#6b6b72" />
                    <stop offset="60%" stopColor="#4a4a50" />
                    <stop offset="100%" stopColor="#2c2c30" />
                </radialGradient>
                <radialGradient id={`lampLensOn-${color}`} cx="38%" cy="32%" r="70%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                    <stop offset="30%" stopColor={color} />
                    <stop offset="100%" stopColor={color} stopOpacity="0.85" />
                </radialGradient>
                {/* Off is deliberately never tinted by `color` - a channel
                    can legitimately be colored black, and an off lamp is
                    just unlit glass regardless of what color it'd glow if
                    it were on. */}
                <radialGradient id="lampLensOff" cx="38%" cy="32%" r="70%">
                    <stop offset="0%" stopColor="#4a4a50" />
                    <stop offset="100%" stopColor="#221f22" />
                </radialGradient>
                {on && (
                    <filter id="lampGlow" x="-60%" y="-60%" width="220%" height="220%">
                        <feGaussianBlur stdDeviation="4" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                )}
            </defs>

            {/* Knurled mounting nut - the panel-mount ring's grip texture */}
            {Array.from({ length: 36 }, (_, i) => i * 10).map((a) => {
                const p1 = polarToCartesian(PILOT_C, PILOT_C, 44, a);
                const p2 = polarToCartesian(PILOT_C, PILOT_C, 48, a);
                return (
                    <line
                        key={`knurl-${a}`}
                        x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
                        stroke="#1f1f22" strokeWidth="2.2"
                    />
                );
            })}

            {/* Bezel body */}
            <circle cx={PILOT_C} cy={PILOT_C} r="42" fill="url(#lampBezel)" />
            <circle cx={PILOT_C} cy={PILOT_C} r="36" fill="none" stroke="#1f1f22" strokeWidth="1.5" />

            {/* Domed lens */}
            <g filter={on ? "url(#lampGlow)" : undefined}>
                <circle
                    cx={PILOT_C}
                    cy={PILOT_C}
                    r="33"
                    fill={on ? `url(#lampLensOn-${color})` : "url(#lampLensOff)"}
                    style={{ transition: "fill 200ms" }}
                />
            </g>

            {/* Concentric fresnel ripples molded into the lens */}
            {ripples.map((r) => (
                <circle
                    key={r}
                    cx={PILOT_C}
                    cy={PILOT_C}
                    r={r}
                    fill="none"
                    stroke={on ? "#ffffff" : "#000000"}
                    strokeOpacity={on ? 0.22 : 0.12}
                    strokeWidth="1"
                />
            ))}
            <circle cx={PILOT_C} cy={PILOT_C} r="33" fill="none" stroke="#00000022" strokeWidth="1.5" />

            {/* Glossy dome highlight - only a lit lens actually catches
                light like this; an off lamp is just an empty, flat circle
                of unlit glass. */}
            {on && (
                <ellipse
                    cx={PILOT_C - 11}
                    cy={PILOT_C - 12}
                    rx="12"
                    ry="7"
                    fill="#ffffff"
                    opacity="0.5"
                    transform={`rotate(-25 ${PILOT_C - 11} ${PILOT_C - 12})`}
                />
            )}
        </svg>
    );
}
